// @ts-nocheck
import * as React from "react";
import { Ic, LineChart, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// Region snapshot history modal. Pulls /api/v1/regions/:id/history?days=30
// and renders one mini-sparkline per metric plus a "지난 30일 대비" delta
// line driven by the server-side trend payload.

const METRICS = [
  { key: "monthly_matches",   label: "이달 매칭",      unit: "건",   color: "var(--orange-500)" },
  { key: "success_rate",      label: "매칭 성공률",    unit: "%",    color: "var(--green-500)",  fmt: v => v?.toFixed(1) },
  { key: "avg_response_mins", label: "평균 응답시간",  unit: "분",   color: "var(--sky-500)",    fmt: v => v?.toFixed(1) },
  { key: "manager_count",     label: "매니저 수",      unit: "명",   color: "var(--green-600)" },
  { key: "org_count",         label: "유관기관 수",    unit: "곳",   color: "var(--amber-500)" },
  { key: "case_count",        label: "위험 케이스",    unit: "건",   color: "var(--rose-500)" },
  { key: "budget_execution",  label: "예산 집행률",    unit: "%",    color: "var(--orange-600)", fmt: v => v?.toFixed(0) },
];

const PERIODS = [
  { key: 7,  label: "7일"  },
  { key: 30, label: "30일" },
  { key: 90, label: "90일" },
];

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function RegionHistoryModal({ region, onClose }) {
  useEscapeKey(onClose);
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true); setErr(null);
    fetch(`/api/v1/regions/${region.id}/history?days=${days}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`오류 (${r.status})`)))
      .then(setData)
      .catch(e => setErr(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [region.id, days]);

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:720, maxHeight:'85vh', padding:0, display:'flex', flexDirection:'column'}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">{region.name} · 운영 지표 이력</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>{region.code} · 일일 스냅샷 시계열</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <div role="tablist" aria-label="기간 선택"
              style={{display:'inline-flex', background:'var(--bg-sub)', borderRadius:8, padding:2, border:'1px solid var(--border-soft)'}}>
              {PERIODS.map(p => {
                const active = p.key === days;
                return (
                  <button key={p.key} type="button" role="tab" aria-selected={active}
                    onClick={()=>setDays(p.key)}
                    style={{padding:'4px 10px', fontSize:11, fontWeight:700, border:'none', borderRadius:6, cursor:'pointer',
                            background: active ? 'var(--surface)' : 'transparent',
                            color:      active ? 'var(--ink-1)'   : 'var(--ink-3)',
                            boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'}}>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
          </div>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, overflow:'auto', flex:1}}>
          {loading && <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>불러오는 중…</div>}
          {!loading && !err && data && (data.series?.length || 0) === 0 && (
            <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>
              스냅샷 이력이 아직 없습니다. 통계 새로고침이 한 번도 실행되지 않은 권역입니다.
            </div>
          )}
          {!loading && !err && data && (data.series?.length || 0) > 0 && (
            <>
              <div className="muted" style={{fontSize:11, marginBottom:10}}>
                {fmtDate(data.series[0].recorded_on)} – {fmtDate(data.series[data.series.length-1].recorded_on)} · {data.series.length}개 샘플
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                {METRICS.map(m => {
                  const series = data.series.map(s => Number(s[m.key]) || 0);
                  const trend = data.trends?.[m.key];
                  const fmt = m.fmt || (v => v);
                  const current = series.length ? series[series.length - 1] : null;
                  const delta = trend?.delta;
                  const deltaSign = delta != null ? (delta > 0 ? "+" : delta < 0 ? "" : "") : "";
                  const deltaColor = delta == null ? "var(--ink-3)"
                    : (m.key === "avg_response_mins" ? (delta < 0 ? "var(--green-600)" : "var(--rose-500)")
                                                     : (delta > 0 ? "var(--green-600)" : delta < 0 ? "var(--rose-500)" : "var(--ink-3)"));
                  return (
                    <div key={m.key} style={{padding:12, background:'var(--bg-sub)', borderRadius:10}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                        <div className="muted" style={{fontSize:11, fontWeight:600}}>{m.label}</div>
                        {trend ? (
                          <div className="mono" style={{fontSize:10, color: deltaColor, fontWeight:700}}>
                            {deltaSign}{fmt(delta)}{m.unit} <span className="muted">vs {days}일 전</span>
                          </div>
                        ) : (
                          <div className="muted" style={{fontSize:10}}>비교 데이터 없음</div>
                        )}
                      </div>
                      <div style={{display:'flex', alignItems:'baseline', gap:6, marginTop:2}}>
                        <div style={{fontSize:20, fontWeight:800, color:m.color}}>{fmt(current)}</div>
                        <div className="muted" style={{fontSize:11}}>{m.unit}</div>
                      </div>
                      <div style={{marginTop:8}}>
                        <LineChart data={series} height={40} color={m.color} fill="var(--bg-sub)"/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
